import { useFormContext } from 'react-hook-form';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { FormContainer } from 'components/layout';
import { Box, Typography } from '@mui/material';
import { AddPackTagButton, ChipInput, PacksWithCreatives } from 'components/common';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useRowsCreatives } from 'features/creatives';
import type { PackEntry } from '../../../types/shared';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Network, TikTokAdType } from '../../../constants';
import datadogActions from 'lib/datadog';

const CREATIVES_LIMIT = 30;

const COLUMNS: GridColDef[] = [
  { field: 'creative_id', headerName: 'Creative ID', width: 100 },
  { field: 'name', headerName: 'Name', width: 450 },
  { field: 'app', headerName: 'App', width: 100 },
  { field: 'creative_type', headerName: 'Type', width: 100 },
  { field: 'created', headerName: 'Created', width: 100 },
];

const CustomFooter = memo<{ total: number; selected: number }>(({ total, selected }) => {
  if (!total) return null;

  const isLimitReached = selected === CREATIVES_LIMIT;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      <Typography variant="body2">{total} found /</Typography>
      {isLimitReached ? (
        <>
          <InfoIcon color="primary" sx={{ fontSize: 21 }} />
          <Typography variant="body2">
            Up to {CREATIVES_LIMIT} creatives can be selected
          </Typography>
        </>
      ) : (
        <Typography variant="body2">{selected} selected</Typography>
      )}
    </Box>
  );
});

CustomFooter.displayName = 'CustomFooter';

const ChooseCreative = memo(() => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const adType: string = watch('adType');
  const selectedCreatives: string[] = watch('selectedCreatives');
  const selectedOnly = watch('selectedCreativesOnly') as boolean;
  const packsWithCreatives: PackEntry[] = watch('packsWithCreatives');
  const activePacksWithCreatives: number = watch('activePacksWithCreatives');
  const isCreativesFieldError: string = errors?.selectedCreatives?.message as string;

  const { rows: creativesList, isLoading, isError, error } = useRowsCreatives({
    params: {
      ad_type: adType ?? TikTokAdType.SINGLE_VIDEO,
      network: Network.TIKTOK,
    },
  });

  const filteredRows = useMemo(() => {
    if (!selectedOnly || !creativesList) return creativesList;
    return creativesList.filter((row) => selectedCreatives.includes(row.id));
  }, [creativesList, selectedCreatives, selectedOnly]);

  const isRowSelectable = useCallback(
    (params: { row: { id: string } }) => {
      return (
        selectedCreatives.length < CREATIVES_LIMIT ||
        selectedCreatives.includes(params.row.id)
      );
    },
    [selectedCreatives],
  );

  const onSelectionModelChange = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (isLoading || !creativesList) return;

      const parsedSelectedRows = selectedRows.slice(0, CREATIVES_LIMIT).map(String);

      const updatedPacks = packsWithCreatives.map((pack, index) =>
        index === activePacksWithCreatives
          ? { ...pack, creatives: parsedSelectedRows }
          : pack,
      );

      const selectedCreativesRows = creativesList.filter((creative) =>
        parsedSelectedRows.includes(creative.id),
      );

      setValue('packsWithCreatives', updatedPacks, { shouldValidate: true });
      setValue('selectedCreativesRows', selectedCreativesRows, { shouldValidate: true });
      setValue('selectedCreatives', parsedSelectedRows, { shouldValidate: true });
    },
    [isLoading, creativesList, packsWithCreatives, activePacksWithCreatives, setValue],
  );

  const footerComponent = useMemo(
    // eslint-disable-next-line react/display-name
    () => () => (
      <CustomFooter
        total={creativesList?.length ?? 0}
        selected={selectedCreatives?.length ?? 0}
      />
    ),
    [creativesList?.length, selectedCreatives?.length],
  );

  useEffect(() => {
    void trigger('packsWithCreatives');
    void trigger('selectedCreatives');
  }, [trigger]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogActions.trackDatadogError(error as Error, {
      source: 'tiktok_choose_creative_query',
      adType: String(adType ?? TikTokAdType.SINGLE_VIDEO),
      network: Network.TIKTOK,
    });
  }, [isError, error, adType]);

  return (
    <FormContainer
      title="Select creatives"
      error={isCreativesFieldError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <PacksWithCreatives />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: '50%' }}>
            <ChipInput
              name={`packsWithCreatives.${activePacksWithCreatives}.filterCreativeByName`}
              inputLabel="Search by name" />
          </Box>
          <Box sx={{ minHeight: 32, mt: 0.5 }}>
            <AddPackTagButton />
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, height: '100%', width: '100%', minHeight: 400, position: 'relative' }}>
        <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
          <DataGrid
            rows={filteredRows ?? []}
            columns={COLUMNS}
            checkboxSelection
            rowSelectionModel={selectedCreatives}
            loading={isLoading}
            onRowSelectionModelChange={onSelectionModelChange}
            hideFooterPagination
            getRowId={(row) => row.id}
            isRowSelectable={isRowSelectable}
            slots={{ footer: footerComponent }}
          />
        </Box>
      </Box>
    </FormContainer>
  );
});

ChooseCreative.displayName = 'ChooseCreative';

export default ChooseCreative;